import { useMemo } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { useTranslation } from "react-i18next";

import { NormalizedTopic } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";
import { Language } from "~/providers/LanguageProvider";

import { useCategoryStore, useDisclosureStore, useHelperStore, useTopicStore } from "~/stores";

const useTopicKeywordSearch = () => {
    const { i18n } = useTranslation();
    const language = i18n.language as Language;

    // Store states
    const topics = useTopicStore(state => state.topics);
    const selectedTopics = useTopicStore(state => state.selectedTopicIds);
    const activeDisclosure = useDisclosureStore(state => state.disclosure);
    const isAllDisclosuresAvailable = useDisclosureStore(state => state.isAllDisclosuresAvailable);
    const keyword = useHelperStore(state => state.keyword);

    // Store dispatch methods
    const dispatchCategory = useCategoryStore(state => state.dispatch);
    const dispatchDisclosure = useDisclosureStore(state => state.dispatch);
    const dispatchTopic = useTopicStore(state => state.dispatch);

    const filteredTopics = useMemo(() => {
        const checkKeywordMatch = (item: NormalizedTopic) => {
            if (!item) return false;

            const { label, disclosureId } = item;
            const topicName = label[language] || "";
            const isDisclosure =
                isAllDisclosuresAvailable || disclosureId === activeDisclosure?.exploreId;

            const isKeywordMatch =
                isDisclosure && topicName?.toLowerCase()?.includes(keyword.toLowerCase());

            return isKeywordMatch;
        };

        return topics.filter(item => checkKeywordMatch(item));
    }, [selectedTopics, keyword, activeDisclosure]);

    const handleTopicClick = (isSelected: boolean, topicItem: NormalizedTopic) => {
        const { count, categoryId, disclosureId, exploreId } = topicItem;

        if (count === -1) {
            return;
        }

        const categoryItemParams = {
            categoryId,
            disclosureId,
            value: exploreId,
            isSelected,
        };

        const disclosureItemParams = {
            disclosureId: disclosureId,
            value: categoryId,
            isSelected,
        };

        unstable_batchedUpdates(() => {
            // Increment/decrement the Disclosures and Categories counts from a selected Topic
            dispatchCategory({
                type: SearchActions.SET_CATEGORY_TOPIC,
                payload: categoryItemParams,
            });
            dispatchDisclosure({
                type: SearchActions.SET_DISCLOSURE_CATEGORY,
                payload: disclosureItemParams,
            });

            // Toggle the checkbox of a selected Topic
            if (isSelected) {
                dispatchTopic({
                    type: SearchActions.SET_SELECTED_TOPIC,
                    payload: topicItem.exploreId,
                });
            } else {
                dispatchTopic({
                    type: SearchActions.REMOVE_SELECTED_TOPIC,
                    payload: topicItem.exploreId,
                });
            }
        });
    };

    return {
        language,
        filteredTopics,
        handleTopicClick,
    };
};

export default useTopicKeywordSearch;
